import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { setOnUnauthorized } from '@/api/client'
import { applyUserLanguage } from '@/i18n'
import { setUser as setSentryUser } from '@/lib/sentry'

import type {
  AuthContextValue,
  BrandUser,
  LoginCredentials,
  RegisterCredentials,
} from './auth.types'
import { brandAuthApi, meToBrandUser } from './brand-auth.api'
import { tokenService } from './token.service'

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BrandUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = user !== null

  useEffect(() => {
    setSentryUser(user ? { id: user.id, email: user.email, username: user.name } : null)
  }, [user])

  // Pulls the current user from GET /me (source of truth for capabilities + ToS).
  const loadUser = useCallback(async (): Promise<BrandUser> => {
    const me = await brandAuthApi.fetchMe()
    const mapped = meToBrandUser(me)
    // The user record owns the UI language, not localStorage — otherwise the
    // previous account's language survives into this session.
    applyUserLanguage(mapped.locale)
    setUser(mapped)
    return mapped
  }, [])

  const clearSession = useCallback(() => {
    tokenService.clearTokens()
    setUser(null)
  }, [])

  const logout = useCallback(() => {
    // Best-effort server logout (revokes all refresh tokens); ignore failures
    // since we clear the local session regardless.
    void brandAuthApi.logout().catch(() => undefined)
    clearSession()
  }, [clearSession])

  // A 401 that survives the client's silent refresh means the session is dead.
  useEffect(() => {
    setOnUnauthorized(clearSession)
  }, [clearSession])

  // Bootstrap: if we hold tokens, hydrate the user from /me (the client will
  // transparently refresh an expired access token).
  useEffect(() => {
    let active = true

    async function bootstrap() {
      if (!tokenService.getToken() && !tokenService.getRefreshToken()) {
        if (active) setIsLoading(false)
        return
      }
      try {
        await loadUser()
      } catch {
        tokenService.clearTokens()
        if (active) setUser(null)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void bootstrap()
    return () => {
      active = false
    }
  }, [loadUser])

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      const { access_token, refresh_token } = await brandAuthApi.login(credentials)
      tokenService.setTokens({ accessToken: access_token, refreshToken: refresh_token })
      await loadUser()
    },
    [loadUser]
  )

  // Brand users are invited (no self-registration); accounts are created via the
  // invite + set-password flow.
  const register = useCallback((_credentials: RegisterCredentials): Promise<void> => {
    return Promise.reject(new Error('Registration is not available — brand users are invited'))
  }, [])

  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      await brandAuthApi.refreshAccessToken()
      await loadUser()
    } catch {
      clearSession()
    }
  }, [loadUser, clearSession])

  const acceptTerms = useCallback(async (): Promise<void> => {
    await brandAuthApi.acceptTerms()
    await loadUser()
  }, [loadUser])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      refreshSession,
      acceptTerms,
    }),
    [user, isAuthenticated, isLoading, login, register, logout, refreshSession, acceptTerms]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
